/**
 * ISO 3166-1 alpha-3 country code -> its alpha-2 code and ISO 4217 currency.
 *
 * GENERATED FILE - do not edit entries by hand. To regenerate, in a scratch dir:
 *
 *   npm i iso-3166-1@2 country-to-currency@3
 *   node -e "const iso=require('iso-3166-1'), c2c=require('country-to-currency');
 *     const m=c2c.default||c2c;
 *     console.log(iso.all().filter(r=>m[r.alpha2]).sort((a,b)=>a.alpha3.localeCompare(b.alpha3))
 *       .map(r=>\`  \${r.alpha3}: { alpha2: "\${r.alpha2}", currency: "\${m[r.alpha2]}" }, // \${r.country}\`)
 *       .join('\n'))"
 *
 * ISO country and currency codes change on the order of once a decade, so this
 * is committed rather than pulled in as a runtime dependency.
 *
 * Used by formatNumber to turn TenantBusiness.country (which is an alpha-3
 * *country* code, not a currency) into the currency and locale a business
 * unit's numbers should render in. Without this, the raw country code was
 * handed to Intl as if it were a currency: codes that happen to also be valid
 * ISO 4217 codes rendered as literal text (Grenada's GRD -> "GRD 12.00L"),
 * and the locale stayed en-IN so every tenant got lakhs and crores.
 *
 * 249 countries.
 */
export type CountryCurrency = {
  alpha2: string;
  currency: string;
};

const COUNTRY_CURRENCY: Record<string, CountryCurrency> = {
  ABW: { alpha2: "AW", currency: "AWG" }, // Aruba
  AFG: { alpha2: "AF", currency: "AFN" }, // Afghanistan
  AGO: { alpha2: "AO", currency: "AOA" }, // Angola
  AIA: { alpha2: "AI", currency: "XCD" }, // Anguilla
  ALA: { alpha2: "AX", currency: "EUR" }, // Åland Islands
  ALB: { alpha2: "AL", currency: "ALL" }, // Albania
  AND: { alpha2: "AD", currency: "EUR" }, // Andorra
  ARE: { alpha2: "AE", currency: "AED" }, // United Arab Emirates
  ARG: { alpha2: "AR", currency: "ARS" }, // Argentina
  ARM: { alpha2: "AM", currency: "AMD" }, // Armenia
  ASM: { alpha2: "AS", currency: "USD" }, // American Samoa
  ATA: { alpha2: "AQ", currency: "USD" }, // Antarctica
  ATF: { alpha2: "TF", currency: "EUR" }, // French Southern Territories
  ATG: { alpha2: "AG", currency: "XCD" }, // Antigua and Barbuda
  AUS: { alpha2: "AU", currency: "AUD" }, // Australia
  AUT: { alpha2: "AT", currency: "EUR" }, // Austria
  AZE: { alpha2: "AZ", currency: "AZN" }, // Azerbaijan
  BDI: { alpha2: "BI", currency: "BIF" }, // Burundi
  BEL: { alpha2: "BE", currency: "EUR" }, // Belgium
  BEN: { alpha2: "BJ", currency: "XOF" }, // Benin
  BES: { alpha2: "BQ", currency: "USD" }, // Bonaire, Sint Eustatius and Saba
  BFA: { alpha2: "BF", currency: "XOF" }, // Burkina Faso
  BGD: { alpha2: "BD", currency: "BDT" }, // Bangladesh
  BGR: { alpha2: "BG", currency: "EUR" }, // Bulgaria
  BHR: { alpha2: "BH", currency: "BHD" }, // Bahrain
  BHS: { alpha2: "BS", currency: "BSD" }, // Bahamas
  BIH: { alpha2: "BA", currency: "BAM" }, // Bosnia and Herzegovina
  BLM: { alpha2: "BL", currency: "EUR" }, // Saint Barthélemy
  BLR: { alpha2: "BY", currency: "BYN" }, // Belarus
  BLZ: { alpha2: "BZ", currency: "BZD" }, // Belize
  BMU: { alpha2: "BM", currency: "BMD" }, // Bermuda
  BOL: { alpha2: "BO", currency: "BOB" }, // Bolivia
  BRA: { alpha2: "BR", currency: "BRL" }, // Brazil
  BRB: { alpha2: "BB", currency: "BBD" }, // Barbados
  BRN: { alpha2: "BN", currency: "BND" }, // Brunei Darussalam
  BTN: { alpha2: "BT", currency: "BTN" }, // Bhutan
  BVT: { alpha2: "BV", currency: "NOK" }, // Bouvet Island
  BWA: { alpha2: "BW", currency: "BWP" }, // Botswana
  CAF: { alpha2: "CF", currency: "XAF" }, // Central African Republic
  CAN: { alpha2: "CA", currency: "CAD" }, // Canada
  CCK: { alpha2: "CC", currency: "AUD" }, // Cocos Islands
  CHE: { alpha2: "CH", currency: "CHF" }, // Switzerland
  CHL: { alpha2: "CL", currency: "CLP" }, // Chile
  CHN: { alpha2: "CN", currency: "CNY" }, // China
  CIV: { alpha2: "CI", currency: "XOF" }, // Côte d'Ivoire
  CMR: { alpha2: "CM", currency: "XAF" }, // Cameroon
  COD: { alpha2: "CD", currency: "CDF" }, // Congo
  COG: { alpha2: "CG", currency: "XAF" }, // Congo
  COK: { alpha2: "CK", currency: "NZD" }, // Cook Islands
  COL: { alpha2: "CO", currency: "COP" }, // Colombia
  COM: { alpha2: "KM", currency: "KMF" }, // Comoros
  CPV: { alpha2: "CV", currency: "CVE" }, // Cabo Verde
  CRI: { alpha2: "CR", currency: "CRC" }, // Costa Rica
  CUB: { alpha2: "CU", currency: "CUP" }, // Cuba
  CUW: { alpha2: "CW", currency: "XCG" }, // Curaçao
  CXR: { alpha2: "CX", currency: "AUD" }, // Christmas Island
  CYM: { alpha2: "KY", currency: "KYD" }, // Cayman Islands
  CYP: { alpha2: "CY", currency: "EUR" }, // Cyprus
  CZE: { alpha2: "CZ", currency: "CZK" }, // Czech Republic
  DEU: { alpha2: "DE", currency: "EUR" }, // Germany
  DJI: { alpha2: "DJ", currency: "DJF" }, // Djibouti
  DMA: { alpha2: "DM", currency: "XCD" }, // Dominica
  DNK: { alpha2: "DK", currency: "DKK" }, // Denmark
  DOM: { alpha2: "DO", currency: "DOP" }, // Dominican Republic
  DZA: { alpha2: "DZ", currency: "DZD" }, // Algeria
  ECU: { alpha2: "EC", currency: "USD" }, // Ecuador
  EGY: { alpha2: "EG", currency: "EGP" }, // Egypt
  ERI: { alpha2: "ER", currency: "ERN" }, // Eritrea
  ESH: { alpha2: "EH", currency: "MAD" }, // Western Sahara
  ESP: { alpha2: "ES", currency: "EUR" }, // Spain
  EST: { alpha2: "EE", currency: "EUR" }, // Estonia
  ETH: { alpha2: "ET", currency: "ETB" }, // Ethiopia
  FIN: { alpha2: "FI", currency: "EUR" }, // Finland
  FJI: { alpha2: "FJ", currency: "FJD" }, // Fiji
  FLK: { alpha2: "FK", currency: "FKP" }, // Falkland Islands
  FRA: { alpha2: "FR", currency: "EUR" }, // France
  FRO: { alpha2: "FO", currency: "DKK" }, // Faroe Islands
  FSM: { alpha2: "FM", currency: "USD" }, // Federated States of Micronesia
  GAB: { alpha2: "GA", currency: "XAF" }, // Gabon
  GBR: { alpha2: "GB", currency: "GBP" }, // United Kingdom of Great Britain and Northern Ireland
  GEO: { alpha2: "GE", currency: "GEL" }, // Georgia
  GGY: { alpha2: "GG", currency: "GBP" }, // Guernsey
  GHA: { alpha2: "GH", currency: "GHS" }, // Ghana
  GIB: { alpha2: "GI", currency: "GIP" }, // Gibraltar
  GIN: { alpha2: "GN", currency: "GNF" }, // Guinea
  GLP: { alpha2: "GP", currency: "EUR" }, // Guadeloupe
  GMB: { alpha2: "GM", currency: "GMD" }, // Gambia
  GNB: { alpha2: "GW", currency: "XOF" }, // Guinea-Bissau
  GNQ: { alpha2: "GQ", currency: "XAF" }, // Equatorial Guinea
  GRC: { alpha2: "GR", currency: "EUR" }, // Greece
  GRD: { alpha2: "GD", currency: "XCD" }, // Grenada
  GRL: { alpha2: "GL", currency: "DKK" }, // Greenland
  GTM: { alpha2: "GT", currency: "GTQ" }, // Guatemala
  GUF: { alpha2: "GF", currency: "EUR" }, // French Guiana
  GUM: { alpha2: "GU", currency: "USD" }, // Guam
  GUY: { alpha2: "GY", currency: "GYD" }, // Guyana
  HKG: { alpha2: "HK", currency: "HKD" }, // Hong Kong
  HMD: { alpha2: "HM", currency: "AUD" }, // Heard Island and McDonald Islands
  HND: { alpha2: "HN", currency: "HNL" }, // Honduras
  HRV: { alpha2: "HR", currency: "EUR" }, // Croatia
  HTI: { alpha2: "HT", currency: "HTG" }, // Haiti
  HUN: { alpha2: "HU", currency: "HUF" }, // Hungary
  IDN: { alpha2: "ID", currency: "IDR" }, // Indonesia
  IMN: { alpha2: "IM", currency: "GBP" }, // Isle of Man
  IND: { alpha2: "IN", currency: "INR" }, // India
  IOT: { alpha2: "IO", currency: "USD" }, // British Indian Ocean Territory
  IRL: { alpha2: "IE", currency: "EUR" }, // Ireland
  IRN: { alpha2: "IR", currency: "IRR" }, // Islamic Republic of Iran
  IRQ: { alpha2: "IQ", currency: "IQD" }, // Iraq
  ISL: { alpha2: "IS", currency: "ISK" }, // Iceland
  ISR: { alpha2: "IL", currency: "ILS" }, // Israel
  ITA: { alpha2: "IT", currency: "EUR" }, // Italy
  JAM: { alpha2: "JM", currency: "JMD" }, // Jamaica
  JEY: { alpha2: "JE", currency: "GBP" }, // Jersey
  JOR: { alpha2: "JO", currency: "JOD" }, // Jordan
  JPN: { alpha2: "JP", currency: "JPY" }, // Japan
  KAZ: { alpha2: "KZ", currency: "KZT" }, // Kazakhstan
  KEN: { alpha2: "KE", currency: "KES" }, // Kenya
  KGZ: { alpha2: "KG", currency: "KGS" }, // Kyrgyzstan
  KHM: { alpha2: "KH", currency: "KHR" }, // Cambodia
  KIR: { alpha2: "KI", currency: "AUD" }, // Kiribati
  KNA: { alpha2: "KN", currency: "XCD" }, // Saint Kitts and Nevis
  KOR: { alpha2: "KR", currency: "KRW" }, // Republic of Korea
  KWT: { alpha2: "KW", currency: "KWD" }, // Kuwait
  LAO: { alpha2: "LA", currency: "LAK" }, // Lao People's Democratic Republic
  LBN: { alpha2: "LB", currency: "LBP" }, // Lebanon
  LBR: { alpha2: "LR", currency: "LRD" }, // Liberia
  LBY: { alpha2: "LY", currency: "LYD" }, // Libya
  LCA: { alpha2: "LC", currency: "XCD" }, // Saint Lucia
  LIE: { alpha2: "LI", currency: "CHF" }, // Liechtenstein
  LKA: { alpha2: "LK", currency: "LKR" }, // Sri Lanka
  LSO: { alpha2: "LS", currency: "LSL" }, // Lesotho
  LTU: { alpha2: "LT", currency: "EUR" }, // Lithuania
  LUX: { alpha2: "LU", currency: "EUR" }, // Luxembourg
  LVA: { alpha2: "LV", currency: "EUR" }, // Latvia
  MAC: { alpha2: "MO", currency: "MOP" }, // Macao
  MAF: { alpha2: "MF", currency: "EUR" }, // Saint Martin
  MAR: { alpha2: "MA", currency: "MAD" }, // Morocco
  MCO: { alpha2: "MC", currency: "EUR" }, // Monaco
  MDA: { alpha2: "MD", currency: "MDL" }, // Republic of Moldova
  MDG: { alpha2: "MG", currency: "MGA" }, // Madagascar
  MDV: { alpha2: "MV", currency: "MVR" }, // Maldives
  MEX: { alpha2: "MX", currency: "MXN" }, // Mexico
  MHL: { alpha2: "MH", currency: "USD" }, // Marshall Islands
  MKD: { alpha2: "MK", currency: "MKD" }, // Macedonia
  MLI: { alpha2: "ML", currency: "XOF" }, // Mali
  MLT: { alpha2: "MT", currency: "EUR" }, // Malta
  MMR: { alpha2: "MM", currency: "MMK" }, // Myanmar
  MNE: { alpha2: "ME", currency: "EUR" }, // Montenegro
  MNG: { alpha2: "MN", currency: "MNT" }, // Mongolia
  MNP: { alpha2: "MP", currency: "USD" }, // Northern Mariana Islands
  MOZ: { alpha2: "MZ", currency: "MZN" }, // Mozambique
  MRT: { alpha2: "MR", currency: "MRU" }, // Mauritania
  MSR: { alpha2: "MS", currency: "XCD" }, // Montserrat
  MTQ: { alpha2: "MQ", currency: "EUR" }, // Martinique
  MUS: { alpha2: "MU", currency: "MUR" }, // Mauritius
  MWI: { alpha2: "MW", currency: "MWK" }, // Malawi
  MYS: { alpha2: "MY", currency: "MYR" }, // Malaysia
  MYT: { alpha2: "YT", currency: "EUR" }, // Mayotte
  NAM: { alpha2: "NA", currency: "NAD" }, // Namibia
  NCL: { alpha2: "NC", currency: "XPF" }, // New Caledonia
  NER: { alpha2: "NE", currency: "XOF" }, // Niger
  NFK: { alpha2: "NF", currency: "AUD" }, // Norfolk Island
  NGA: { alpha2: "NG", currency: "NGN" }, // Nigeria
  NIC: { alpha2: "NI", currency: "NIO" }, // Nicaragua
  NIU: { alpha2: "NU", currency: "NZD" }, // Niue
  NLD: { alpha2: "NL", currency: "EUR" }, // Netherlands
  NOR: { alpha2: "NO", currency: "NOK" }, // Norway
  NPL: { alpha2: "NP", currency: "NPR" }, // Nepal
  NRU: { alpha2: "NR", currency: "AUD" }, // Nauru
  NZL: { alpha2: "NZ", currency: "NZD" }, // New Zealand
  OMN: { alpha2: "OM", currency: "OMR" }, // Oman
  PAK: { alpha2: "PK", currency: "PKR" }, // Pakistan
  PAN: { alpha2: "PA", currency: "PAB" }, // Panama
  PCN: { alpha2: "PN", currency: "NZD" }, // Pitcairn
  PER: { alpha2: "PE", currency: "PEN" }, // Peru
  PHL: { alpha2: "PH", currency: "PHP" }, // Philippines
  PLW: { alpha2: "PW", currency: "USD" }, // Palau
  PNG: { alpha2: "PG", currency: "PGK" }, // Papua New Guinea
  POL: { alpha2: "PL", currency: "PLN" }, // Poland
  PRI: { alpha2: "PR", currency: "USD" }, // Puerto Rico
  PRK: { alpha2: "KP", currency: "KPW" }, // Democratic People's Republic of Korea
  PRT: { alpha2: "PT", currency: "EUR" }, // Portugal
  PRY: { alpha2: "PY", currency: "PYG" }, // Paraguay
  PSE: { alpha2: "PS", currency: "ILS" }, // State of Palestine
  PYF: { alpha2: "PF", currency: "XPF" }, // French Polynesia
  QAT: { alpha2: "QA", currency: "QAR" }, // Qatar
  REU: { alpha2: "RE", currency: "EUR" }, // Réunion
  ROU: { alpha2: "RO", currency: "RON" }, // Romania
  RUS: { alpha2: "RU", currency: "RUB" }, // Russian Federation
  RWA: { alpha2: "RW", currency: "RWF" }, // Rwanda
  SAU: { alpha2: "SA", currency: "SAR" }, // Saudi Arabia
  SDN: { alpha2: "SD", currency: "SDG" }, // Sudan
  SEN: { alpha2: "SN", currency: "XOF" }, // Senegal
  SGP: { alpha2: "SG", currency: "SGD" }, // Singapore
  SGS: { alpha2: "GS", currency: "FKP" }, // South Georgia and the South Sandwich Islands
  SHN: { alpha2: "SH", currency: "SHP" }, // Saint Helena, Ascension and Tristan da Cunha
  SJM: { alpha2: "SJ", currency: "NOK" }, // Svalbard and Jan Mayen
  SLB: { alpha2: "SB", currency: "SBD" }, // Solomon Islands
  SLE: { alpha2: "SL", currency: "SLE" }, // Sierra Leone
  SLV: { alpha2: "SV", currency: "USD" }, // El Salvador
  SMR: { alpha2: "SM", currency: "EUR" }, // San Marino
  SOM: { alpha2: "SO", currency: "SOS" }, // Somalia
  SPM: { alpha2: "PM", currency: "EUR" }, // Saint Pierre and Miquelon
  SRB: { alpha2: "RS", currency: "RSD" }, // Serbia
  SSD: { alpha2: "SS", currency: "SSP" }, // South Sudan
  STP: { alpha2: "ST", currency: "STN" }, // Sao Tome and Principe
  SUR: { alpha2: "SR", currency: "SRD" }, // Suriname
  SVK: { alpha2: "SK", currency: "EUR" }, // Slovakia
  SVN: { alpha2: "SI", currency: "EUR" }, // Slovenia
  SWE: { alpha2: "SE", currency: "SEK" }, // Sweden
  SWZ: { alpha2: "SZ", currency: "SZL" }, // Swaziland
  SXM: { alpha2: "SX", currency: "XCG" }, // Sint Maarten
  SYC: { alpha2: "SC", currency: "SCR" }, // Seychelles
  SYR: { alpha2: "SY", currency: "SYP" }, // Syrian Arab Republic
  TCA: { alpha2: "TC", currency: "USD" }, // Turks and Caicos Islands
  TCD: { alpha2: "TD", currency: "XAF" }, // Chad
  TGO: { alpha2: "TG", currency: "XOF" }, // Togo
  THA: { alpha2: "TH", currency: "THB" }, // Thailand
  TJK: { alpha2: "TJ", currency: "TJS" }, // Tajikistan
  TKL: { alpha2: "TK", currency: "NZD" }, // Tokelau
  TKM: { alpha2: "TM", currency: "TMT" }, // Turkmenistan
  TLS: { alpha2: "TL", currency: "USD" }, // Timor-Leste
  TON: { alpha2: "TO", currency: "TOP" }, // Tonga
  TTO: { alpha2: "TT", currency: "TTD" }, // Trinidad and Tobago
  TUN: { alpha2: "TN", currency: "TND" }, // Tunisia
  TUR: { alpha2: "TR", currency: "TRY" }, // Turkey
  TUV: { alpha2: "TV", currency: "AUD" }, // Tuvalu
  TWN: { alpha2: "TW", currency: "TWD" }, // Taiwan, Province of China
  TZA: { alpha2: "TZ", currency: "TZS" }, // United Republic of Tanzania
  UGA: { alpha2: "UG", currency: "UGX" }, // Uganda
  UKR: { alpha2: "UA", currency: "UAH" }, // Ukraine
  UMI: { alpha2: "UM", currency: "USD" }, // United States Minor Outlying Islands
  URY: { alpha2: "UY", currency: "UYU" }, // Uruguay
  USA: { alpha2: "US", currency: "USD" }, // United States of America
  UZB: { alpha2: "UZ", currency: "UZS" }, // Uzbekistan
  VAT: { alpha2: "VA", currency: "EUR" }, // Holy See
  VCT: { alpha2: "VC", currency: "XCD" }, // Saint Vincent and the Grenadines
  VEN: { alpha2: "VE", currency: "VES" }, // Venezuela (Bolivarian Republic of)
  VGB: { alpha2: "VG", currency: "USD" }, // Virgin Islands
  VIR: { alpha2: "VI", currency: "USD" }, // Virgin Islands of the United States
  VNM: { alpha2: "VN", currency: "VND" }, // Viet Nam
  VUT: { alpha2: "VU", currency: "VUV" }, // Vanuatu
  WLF: { alpha2: "WF", currency: "XPF" }, // Wallis and Futuna
  WSM: { alpha2: "WS", currency: "WST" }, // Samoa
  YEM: { alpha2: "YE", currency: "YER" }, // Yemen
  ZAF: { alpha2: "ZA", currency: "ZAR" }, // South Africa
  ZMB: { alpha2: "ZM", currency: "ZMW" }, // Zambia
  ZWE: { alpha2: "ZW", currency: "ZWG" }, // Zimbabwe
};

export default COUNTRY_CURRENCY;
